import { useState, useEffect } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { Loader2, AlertCircle } from 'lucide-react'
import { useFileIO } from '@papan/files/client/file-io'
import { useOnlineRecovery } from '@papan/realtime/client'
import { DataGridViewerInner, parseParquet } from './DataGridViewer'
import { errorText } from '@papan/api-client'

interface ParquetViewerProps {
  fileId: string
  fileName?: string
  /** Omit to render the file's latest version (share view / live drive). */
  versionId?: string
}

/**
 * Parquet viewer — fetches the raw parquet file (latest version or a
 * specific version when versionId is set), parses with hyparquet, and
 * renders in a read-only DataGridViewerInner.
 */
export function ParquetViewer({ fileId, fileName, versionId }: ParquetViewerProps) {
  const [data, setData] = useState<{ columns: string[]; rows: Record<string, unknown>[] }>({ columns: [], rows: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // #1316: a dropped answer (its session ended) is asked again as the session now here — never shown as an error
  // Review round 2: in the share view an end that MOVES the share session leaves the ended sid in use until the new
  // session lands — the re-ask waits for it there (`useDroppedLoadReask`), never asking as the ended session
  const { reask, begin } = useDroppedLoadReask()
  const recovery = useOnlineRecovery()
  const fileIO = useFileIO()

  useEffect(() => {
    const asked = begin()   // the share session this load goes out as
    setLoading(true)
    setError(null)

    fileIO.loadContent(fileId, versionId)
      .then(async (blob) => {
        const buffer = await blob.arrayBuffer()
        const result = await parseParquet(buffer)
        result.rows.forEach((r, i) => { r.__rowIdx = i })
        setData(result)
        setLoading(false)
      })
      .catch(err => {
        const text = errorText(err, 'Failed to load parquet file')
        if (text === null) { asked.dropped(); return }
        setError(text)
        setLoading(false)
      })
  }, [fileId, versionId, recovery, fileIO, reask, begin])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin themed-text-muted" size={24} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <AlertCircle size={32} className="mx-auto mb-2 text-red-400 opacity-60" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <DataGridViewerInner
      fileId={fileId}
      fileName={fileName}
      previewVersionId={versionId || 'current'}
      columns={data.columns}
      rows={data.rows}
      loading={false}
      error={null}
    />
  )
}
